import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import InstagramEmbed from "@/components/InstagramEmbed";
import { useAuth } from "@/context/AuthContext";
import { Star, Instagram, MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";

const resolveImg = (img) => (img?.startsWith("http") ? img : `${API}/files/${img}`);

function MessageModal({ seller, onClose }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const send = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: seller.id, content });
      toast.success(`Message sent to ${seller.name}`);
      onClose();
      navigate(`/messages/${seller.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="message-modal" className="bg-card border border-border rounded-sm p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-2">◦ Direct Message</div>
            <h3 className="font-serif text-2xl">Message {seller.display_name || seller.name}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={send}>
          <textarea
            data-testid="message-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required minLength={1}
            placeholder="Hi! I love your work — quick question…"
            rows={6}
            className="w-full bg-transparent border border-border rounded-sm p-4 text-sm focus:outline-none focus:border-primary"
          />
          <div className="flex justify-end gap-3 mt-5">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm border border-border rounded-sm">Cancel</button>
            <button data-testid="message-send-btn" disabled={sending || !content.trim()} className="btn-primary px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60">
              <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/sellers/${id}`).then(({ data }) => setSeller(data)).catch(() => setSeller(false));
  }, [id]);

  if (seller === null) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (seller === false) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Seller not found.</div>;

  const igHandle = seller.instagram?.replace(/^@/, "");
  const isSelf = user && user.id === seller.id;

  return (
    <div data-testid="seller-profile" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      {seller.banner && (
        <img src={resolveImg(seller.banner)} alt="" data-testid="seller-banner" className="w-full h-44 lg:h-64 object-cover rounded-sm mb-10 lg:mb-14" />
      )}
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
        <div className="lg:col-span-7">
          {seller.logo && (
            <img src={resolveImg(seller.logo)} alt="" data-testid="seller-logo" className="w-20 h-20 rounded-full object-cover border border-border mb-5" />
          )}
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Maker Profile</div>
          {seller.is_founding && (
            <span className="founding-badge inline-block text-[10px] tracking-[0.15em] uppercase font-semibold px-3 py-1 rounded-sm mb-5">
              ◦ Founding Seller
            </span>
          )}
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight font-medium leading-[1.05]">{seller.display_name || seller.name}</h1>
          {seller.business_name && seller.name && seller.business_name !== seller.name && (
            <div className="mt-3 text-sm text-muted-foreground italic">Run by {seller.name}</div>
          )}
          {seller.bio && <p className="mt-8 text-lg text-muted-foreground leading-relaxed max-w-xl">{seller.bio}</p>}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            {!isSelf && (
              <button
                onClick={() => user ? setShowMessage(true) : navigate("/login")}
                data-testid="message-seller-btn"
                className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 text-sm rounded-sm"
              >
                <MessageCircle className="w-4 h-4" /> Message seller
              </button>
            )}
            {igHandle && (
              <a
                href={`https://instagram.com/${igHandle}`}
                target="_blank" rel="noreferrer"
                data-testid="seller-instagram-link"
                className="btn-outline inline-flex items-center gap-2 px-6 py-3.5 text-sm rounded-sm"
              >
                <Instagram className="w-4 h-4" /> @{igHandle}
              </a>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-4 lg:gap-8 lg:pl-12">
          <div className="border-t border-border pt-4">
            <div className="font-serif text-3xl lg:text-4xl">{seller.stats.product_count}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1 font-medium">Pieces</div>
          </div>
          <div className="border-t border-border pt-4">
            <div className="font-serif text-3xl lg:text-4xl">{seller.stats.orders}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1 font-medium">Orders</div>
          </div>
          <div className="border-t border-border pt-4">
            <div className="font-serif text-3xl lg:text-4xl flex items-baseline gap-1">
              {seller.stats.reviews > 0 ? seller.stats.avg_rating.toFixed(1) : "—"}
              {seller.stats.reviews > 0 && <Star className="w-4 h-4 fill-primary text-primary" />}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1 font-medium">{seller.stats.reviews} reviews</div>
          </div>
        </div>
      </section>

      {/* Instagram embed strip */}
      {(seller.instagram_posts?.length > 0 || igHandle) && (
        <section className="mt-20 border-y border-border py-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-2">◦ Behind the scenes</div>
              <h3 className="font-serif text-2xl lg:text-3xl">{igHandle ? `Latest from @${igHandle}` : "From the studio"}</h3>
            </div>
            {igHandle && (
              <a href={`https://instagram.com/${igHandle}`} target="_blank" rel="noreferrer" className="text-xs tracking-[0.2em] uppercase font-semibold hover:text-primary transition-colors">
                Follow on Instagram →
              </a>
            )}
          </div>
          {seller.instagram_posts?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {seller.instagram_posts.slice(0, 4).map((url, i) => (
                <InstagramEmbed key={i} url={url} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {seller.name === user?.name
                ? "Add your favourite Instagram posts in your dashboard to feature them here."
                : `Follow @${igHandle} for behind-the-scenes from the studio.`}
            </p>
          )}
        </section>
      )}

      {/* Products */}
      <section className="mt-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Handmade Collection</div>
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight">From the studio.</h2>
          </div>
        </div>
        {seller.products.length === 0 ? (
          <div className="text-muted-foreground italic">No listings yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {seller.products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {showMessage && <MessageModal seller={seller} onClose={() => setShowMessage(false)} />}
    </div>
  );
}
